import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogClose } from '@angular/material/dialog';
import { DashboardComponent } from '../dashboard/dashboard.component';
import { EjournalService } from '../../services/ejournal.service';
import { EJOURNAL_SESSION_PROVIDERS } from '../../ejournal-session.providers';
import { Packet } from '@escrowtab/packets-api-client';
import { ProfileDto } from '@escrowtab/v2profile-api-client';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import { AuditAction } from '@et/typings';

export interface DashboardModalData {
  packet: Packet;
  profile: ProfileDto;
  auditActions?: AuditAction[];
  pageNamesByNumber?: Record<number, string>;
  /** Optional in-memory packet PDF; when set, skips network fetch for seal preview. */
  packetFile?: File;
}

@Component({
  selector: 'et-journal-dashboard-modal',
  imports: [MatDialogClose, DashboardComponent, PresentationalAtomsModule],
  templateUrl: './dashboard-modal.component.html',
  styleUrl: './dashboard-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...EJOURNAL_SESSION_PROVIDERS],
})
export class DashboardModalComponent implements OnInit {
  private readonly data = inject<DashboardModalData>(MAT_DIALOG_DATA);
  private readonly ejournalService = inject(EjournalService);

  isLoading = this.ejournalService.eJournalLoading;

  get packet() {
    return this.data.packet;
  }

  ngOnInit(): void {
    this.loadJournal(
      this.data.packet,
      this.data.profile,
      this.data.auditActions,
      this.data.pageNamesByNumber,
      this.data.packetFile,
    );
  }

  /**
   * Start loading the journal by setting the profile and selected packet.
   * When signing-state audit actions are provided, merges them for eSeal derivation.
   * When page names are provided (from markup), skips the markup API fetch.
   * When packetFile is provided, caches it for seal PDF preview.
   *
   * @param packet - The packet to load
   * @param profile - The profile to load
   * @param auditActions - Optional signing-state audit actions for eSeals
   * @param pageNamesByNumber - Optional page → document name map from markup
   * @param packetFile - Optional in-memory packet PDF
   */
  private loadJournal(
    packet: Packet,
    profile: ProfileDto,
    auditActions?: AuditAction[],
    pageNamesByNumber?: Record<number, string>,
    packetFile?: File,
  ) {
    this.ejournalService.setProfile(profile);
    this.ejournalService.setSelectedPacket(packet);
    if (auditActions?.length) {
      this.ejournalService.loadNotarizedAuditActionsFromState(auditActions);
    }
    if (pageNamesByNumber) {
      this.ejournalService.setPageNamesByNumber(pageNamesByNumber);
    }
    if (packetFile) {
      this.ejournalService.setPacketPdfFile(packetFile);
    }
  }
}
